import { useEffect, useState, type ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { PreviewDeviceMode } from "../types";

const VIDEO_URL_RE = /\.(mp4|mov|avi|webm|m4v)(\?|$)/i;

export function PreviewShell({
  device,
  children,
  mobileFrame = true,
}: {
  device: PreviewDeviceMode;
  children: ReactNode;
  mobileFrame?: boolean;
}) {
  if (device === "desktop") {
    return (
      <div className="w-full max-w-[420px] bg-white rounded-xl shadow-lg overflow-hidden">
        {children}
      </div>
    );
  }
  if (!mobileFrame) {
    return (
      <div className="w-[300px] bg-white rounded-3xl shadow-lg overflow-hidden">{children}</div>
    );
  }
  return (
    <div className="w-[300px] rounded-[2rem] border-[10px] border-black/80 bg-black overflow-hidden shadow-2xl">
      <div className="bg-white">{children}</div>
    </div>
  );
}

export function MediaSlot({
  mediaUrl,
  mediaType,
  aspect = "aspect-square",
  fallback,
  bgClass = "bg-gray-100",
}: {
  mediaUrl?: string;
  mediaType?: "image" | "video";
  aspect?: string;
  fallback?: ReactNode;
  bgClass?: string;
}) {
  if (!mediaUrl) {
    return (
      <div className={`w-full ${aspect} ${bgClass} flex items-center justify-center`}>
        {fallback}
      </div>
    );
  }
  return (
    <div className={`w-full ${aspect} ${bgClass} overflow-hidden`}>
      {mediaType === "video" ? (
        <video src={mediaUrl} className="w-full h-full object-cover" muted playsInline />
      ) : (
        <img
          src={mediaUrl}
          alt=""
          className="w-full h-full object-cover"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = "none";
          }}
        />
      )}
    </div>
  );
}

// Carousel slider used by IG/FB/TikTok previews when a post has >1 ordered media
// items. Reflects the composer's mediaUrls order live (reorder updates the props).
export function MediaCarousel({
  mediaUrls,
  aspect = "aspect-square",
  bgClass = "bg-gray-100",
  fallback,
}: {
  mediaUrls: string[];
  aspect?: string;
  fallback?: ReactNode;
  bgClass?: string;
}) {
  const [index, setIndex] = useState(0);
  const count = mediaUrls.length;

  // Keep index valid when items are removed/reordered down.
  useEffect(() => {
    if (index > count - 1) setIndex(Math.max(0, count - 1));
  }, [count, index]);

  if (count === 0) {
    return (
      <div className={`w-full ${aspect} ${bgClass} flex items-center justify-center`}>
        {fallback}
      </div>
    );
  }

  const safeIndex = Math.min(index, count - 1);
  const url = mediaUrls[safeIndex];
  const isVideo = VIDEO_URL_RE.test(url);
  const go = (delta: number) => setIndex((prev) => (prev + delta + count) % count);

  return (
    <div className={`relative w-full ${aspect} ${bgClass} overflow-hidden`}>
      {isVideo ? (
        <video src={url} className="w-full h-full object-cover" muted playsInline />
      ) : (
        <img
          src={url}
          alt=""
          className="w-full h-full object-cover"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = "none";
          }}
        />
      )}
      {count > 1 && (
        <>
          <button
            type="button"
            onClick={() => go(-1)}
            className="absolute left-1.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-black/45 text-white flex items-center justify-center hover:bg-black/65"
            aria-label="prev"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-black/45 text-white flex items-center justify-center hover:bg-black/65"
            aria-label="next"
          >
            <ChevronRight size={14} />
          </button>
          <div className="absolute top-2 right-2 rounded-full bg-black/55 px-1.5 py-0.5 text-[10px] font-medium text-white">
            {safeIndex + 1}/{count}
          </div>
          <div className="absolute bottom-2 left-0 right-0 flex items-center justify-center gap-1">
            {mediaUrls.map((item, i) => (
              <button
                key={item}
                type="button"
                onClick={() => setIndex(i)}
                className={`h-1.5 w-1.5 rounded-full transition-colors ${
                  i === safeIndex ? "bg-white" : "bg-white/50"
                }`}
                aria-label={`slide ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
